// Progresso do mês de um alojamento e escolha de qual das 4 vistorias fazer.
// Protótipo: showCompetenciaScreen (1347) e escolherSemana (1502).

import type { VistoriaResumo } from './tipos.js';

export const META_MENSAL = 4;

/** Mais recente primeiro (ordem em que o protótipo guardava a lista: mergeSubs). */
export function ordenarRecentes<T extends { enviadaEm: string }>(lista: readonly T[]): T[] {
  return [...lista].sort((a, b) => Date.parse(b.enviadaEm) - Date.parse(a.enviadaEm));
}

/** Vistorias que contam para a meta: tudo que não foi reprovado (protótipo: validas). */
export function validas<T extends { status: string }>(lista: readonly T[]): T[] {
  return lista.filter((v) => v.status !== 'reprovada');
}

export interface ProgressoMes {
  feitas: number;
  faltam: number;
  aprovadas: number;
  aguardando: number;
  reprovadas: VistoriaResumo[];
  completo: boolean;
  /** Vistoria que ocupa cada uma das 4 posições (1..4). */
  porSemana: Partial<Record<1 | 2 | 3 | 4, VistoriaResumo>>;
}

/** @param doMes vistorias do alojamento na competência (qualquer ordem). */
export function progressoDoMes(doMes: readonly VistoriaResumo[]): ProgressoMes {
  const lista = ordenarRecentes(doMes);
  const feitas = validas(lista).length;
  const porSemana: ProgressoMes['porSemana'] = {};
  for (const v of lista) {
    const k = v.semana;
    if (k !== null && k >= 1 && k <= 4 && !porSemana[k as 1 | 2 | 3 | 4]) porSemana[k as 1 | 2 | 3 | 4] = v;
  }
  for (const v of lista) {
    const k = v.semana;
    if (k !== null && k >= 1 && k <= 4) continue;
    for (const p of [1, 2, 3, 4] as const) {
      if (!porSemana[p]) {
        porSemana[p] = v;
        break;
      }
    }
  }
  return {
    feitas,
    faltam: Math.max(0, META_MENSAL - feitas),
    aprovadas: lista.filter((v) => v.status === 'aprovada').length,
    aguardando: lista.filter((v) => v.status === 'pendente').length,
    reprovadas: lista.filter((v) => v.status === 'reprovada'),
    completo: feitas >= META_MENSAL,
    porSemana,
  };
}

/**
 * Qual vistoria vem pré-selecionada: mantém a atual se o usuário escolheu (ou se está livre);
 * senão a 1ª reprovada (para refazer), senão a 1ª livre, senão a atual ou a 1ª.
 */
export function sugerirSemana(p: ProgressoMes, atual: number | null, escolhaManual: boolean): number {
  const ocupada = (n: number) => {
    const v = p.porSemana[n as 1 | 2 | 3 | 4];
    return !!v && v.status !== 'reprovada';
  };
  if (atual && (escolhaManual || !ocupada(atual))) return atual;
  for (const n of [1, 2, 3, 4] as const) if (p.porSemana[n]?.status === 'reprovada') return n;
  for (const n of [1, 2, 3, 4] as const) if (!p.porSemana[n]) return n;
  return atual || 1;
}

/**
 * Situação de uma posição já enviada, para o aviso de confirmação.
 * null = livre; 'aprovada' = "já foi aprovada"; 'aguardando' = "já foi enviada e está aguardando".
 */
export function posicaoJaEnviada(doMes: readonly VistoriaResumo[], n: number): 'aprovada' | 'aguardando' | null {
  const feitas = doMes.filter((v) => v.status !== 'reprovada' && v.semana === n);
  if (!feitas.length) return null;
  return feitas.some((v) => v.status === 'aprovada') ? 'aprovada' : 'aguardando';
}
